/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Spinner } from '@/shared/components/feedback/Spinner';
import { AppLayout } from '../layout/AppLayout';
import { RequireAuth } from '../guards/RequireAuth';

const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const SetupAccountPage = lazy(() => import('@/features/auth/SetupAccountPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const ChatbotsPage = lazy(() => import('@/features/chatbots/ChatbotsPage'));
const ChatbotRequestsPage = lazy(() => import('@/features/chatbot-requests/ChatbotRequestsPage'));
const SupportRequestsPage = lazy(() => import('@/features/support-requests/SupportRequestsPage'));
const ConversationPage = lazy(() => import('@/features/conversations/ConversationPage'));

function Fallback() {
  return (
    <div className="grid min-h-screen place-items-center bg-canvas">
      <Spinner size="lg" />
    </div>
  );
}

/**
 * Arbre de routes — point de coordination inter-plans.
 * Les plans B (demandes/support/interactions) et C (chatbots/livrables)
 * AJOUTERONT leurs routes dans le layout protégé ci-dessous.
 */
export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <Suspense fallback={<Fallback />}>
        <LoginPage />
      </Suspense>
    ),
  },
  // Page publique de paramétrage de compte client (lien d'invitation /
  // réinitialisation du groupe madatalk.client) — hors RequireAuth, ne
  // redirige jamais vers la console ONIRIA.
  {
    path: '/auth/set-password',
    element: (
      <Suspense fallback={<Fallback />}>
        <SetupAccountPage />
      </Suspense>
    ),
  },
  {
    element: <RequireAuth />,
    children: [
      {
        path: '/',
        element: (
          <Suspense fallback={<Fallback />}>
            <AppLayout />
          </Suspense>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          // Plan C : liste agrégée des chatbots + livrables + pause/résiliation.
          {
            path: 'chatbots',
            element: (
              <Suspense fallback={<Fallback />}>
                <ChatbotsPage />
              </Suspense>
            ),
          },
          // Plan B : demandes de création de chatbot + suivi/échanges.
          {
            path: 'chatbot-requests',
            element: (
              <Suspense fallback={<Fallback />}>
                <ChatbotRequestsPage />
              </Suspense>
            ),
          },
          // Plan B : demandes support (liste) + mode création (dialogue auto,
          // pré-rempli via `?chatbotId=` depuis la liste « mes chatbots »).
          {
            path: 'support-requests',
            element: (
              <Suspense fallback={<Fallback />}>
                <SupportRequestsPage />
              </Suspense>
            ),
          },
          {
            path: 'support-requests/new',
            element: (
              <Suspense fallback={<Fallback />}>
                <SupportRequestsPage />
              </Suspense>
            ),
          },
          // Plan conversation dédiée SPA : interface conversation d'une demande
          // (chatbot/support), ouverte par le bouton « Conversation » des lignes.
          {
            path: 'conversation',
            element: (
              <Suspense fallback={<Fallback />}>
                <ConversationPage />
              </Suspense>
            ),
          },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
